import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaGavel, FaBuilding, FaShieldAlt, FaUserCheck, FaClipboardCheck,
  FaPhoneAlt, FaHandshake, FaArrowLeft, FaMapMarkerAlt, FaEnvelope,
  FaPhone, FaGlobe
} from 'react-icons/fa';
import './CountyContacts.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CountyContacts = () => {
  const { county } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCountyContacts();
  }, [county]);

  const fetchCountyContacts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/address-book/county/${county}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setError('Failed to load county contacts');
      }
    } catch (err) {
      console.error('Error fetching county contacts:', err);
      setError('Failed to load county contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate('/address-book');
  };

  const handleContactClick = (type, id) => {
    const routeMap = {
      courts: '/address-book/courts',
      jails: '/address-book/jails',
      sheriffOffices: '/address-book/sheriff-offices',
      probationParole: '/address-book/probation-parole',
      pretrialServices: '/address-book/pretrial-services',
      dispatchCenters: '/address-book/dispatch-centers',
      bailAgencies: '/address-book/bail-agencies'
    };
    navigate(`${routeMap[type]}/${id}`);
  };

  const renderContactCard = (contact, type, icon, color) => {
    const Icon = icon;
    return (
      <div
        key={contact.id}
        className="county-contact-card"
        onClick={() => handleContactClick(type, contact.id)}
        style={{ '--card-color': color }}
      >
        <div className="contact-icon-wrapper">
          <Icon className="contact-icon" />
        </div>
        <div className="contact-info">
          <h3 className="contact-name">{contact.name || contact.facility_name}</h3>
          <div className="contact-details">
            {contact.address_street && (
              <div className="detail-item">
                <FaMapMarkerAlt className="detail-icon" />
                <span>{contact.address_street}, {contact.city || contact.address_city}</span>
              </div>
            )}
            {contact.phone && (
              <div className="detail-item">
                <FaPhone className="detail-icon" />
                <span>{contact.phone}</span>
              </div>
            )}
            {contact.email && (
              <div className="detail-item">
                <FaEnvelope className="detail-icon" />
                <span>{contact.email}</span>
              </div>
            )}
            {contact.website && (
              <div className="detail-item">
                <FaGlobe className="detail-icon" />
                <a href={contact.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                  Visit Website
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSection = (title, contacts, type, icon, color) => {
    if (!contacts || contacts.length === 0) return null;

    return (
      <div className="county-section">
        <div className="section-header" style={{ borderLeftColor: color }}>
          <h2 className="section-title">{title}</h2>
          <span className="section-count">{contacts.length}</span>
        </div>
        <div className="section-contacts">
          {contacts.map(contact => renderContactCard(contact, type, icon, color))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="county-contacts-container">
        <div className="loading">Loading {county} County contacts...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="county-contacts-container">
        <div className="error-message">{error || 'No data available'}</div>
        <button onClick={handleBackClick} className="back-button">
          <FaArrowLeft /> Back to Address Book
        </button>
      </div>
    );
  }

  return (
    <div className="county-contacts-container">
      <div className="county-header">
        <button onClick={handleBackClick} className="back-button">
          <FaArrowLeft /> Back
        </button>
        <div className="header-content">
          <h1>{county} County Contacts</h1>
          <p className="subtitle">All justice system contacts in {county} County</p>
        </div>
        <div className="header-stats">
          <div className="total-count">
            <span className="count-number">{data.total}</span>
            <span className="count-label">Total Contacts</span>
          </div>
        </div>
      </div>

      <div className="county-sections">
        {renderSection('Courts', data.courts, 'courts', FaGavel, '#2c3e50')}
        {renderSection('Jails & Detention Centers', data.jails, 'jails', FaBuilding, '#34495e')}
        {renderSection("Sheriff's Offices", data.sheriffOffices, 'sheriffOffices', FaShieldAlt, '#2980b9')}
        {renderSection('Probation & Parole', data.probationParole, 'probationParole', FaUserCheck, '#d35400')}
        {renderSection('Pretrial Services', data.pretrialServices, 'pretrialServices', FaClipboardCheck, '#8e44ad')}
        {renderSection('Dispatch Centers', data.dispatchCenters, 'dispatchCenters', FaPhoneAlt, '#e74c3c')}
        {renderSection('Bail Bond Agencies', data.bailAgencies, 'bailAgencies', FaHandshake, '#f39c12')}
      </div>

      {data.total === 0 && (
        <div className="no-contacts">
          <p>No contacts found for {county} County</p>
        </div>
      )}
    </div>
  );
};

export default CountyContacts;
